/**
 * The Versions tab: one drawing's history, and the way between versions.
 *
 * chat1.md wireframe 2 gives this tab a "Compare to current" action on every row. It is **not
 * rendered**, and not as a disabled control either: compare is `KLQ-20260820-002` and none of
 * its backend exists, so a greyed button would be a promise with a date nobody has set.
 *
 * Every row prints `identifier` — the name the *server* minted. `hierarchy/naming.py` is the
 * only implementation of the `{rev}` grammar anywhere, and a `r${counter}` assembled here
 * would quietly be the second one.
 */

import type { Version } from './api';
import { Pill } from '@/components';

import styles from './Viewer.module.css';
import { useMessages } from '@/lib/i18n';

export interface VersionsRailProps {
  readonly versions: readonly Version[];
  readonly openVersionId: string | null;
  readonly onOpen: (versionId: string) => void;
  readonly loading: boolean;
}

export function VersionsRail({ versions, openVersionId, onOpen, loading }: VersionsRailProps) {
  const t = useMessages('drawings');

  if (loading) return <p className={styles.muted}>{t('viewer.versions.loading')}</p>;
  if (versions.length === 0) return <p className={styles.muted}>{t('viewer.versions.empty')}</p>;

  return (
    <ul className={styles.versions}>
      {versions.map((version) => (
        <li key={version.id}>
          <button
            type="button"
            className={[styles.version, version.id === openVersionId ? styles.versionOpen : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              onOpen(version.id);
            }}
          >
            <span className={styles.versionHead}>
              <span className={styles.versionName}>{version.identifier}</span>
              {version.is_current ? (
                <Pill tone="success">{t('viewer.current')}</Pill>
              ) : null}
              <span className={styles.versionMeta}>
                {t('viewer.versions.published', { at: new Date(version.published_at) })}
              </span>
            </span>
            {version.version_note === '' ? null : (
              <p className={styles.versionNote}>{version.version_note}</p>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
